/**
 * DropboxBrowser — native, in-page file explorer for a property's Dropbox folder.
 *
 * Reusable: drives off the admin clc-properties Dropbox endpoints (list / file / thumbnail),
 * which proxy bytes through the backend and scope every request to the property's root folder.
 * Mount it anywhere you have a hostawayListingId; pass canManage for future upload/delete actions.
 *
 * v1: breadcrumb navigation, folders-first list with type icons, inline preview of PDFs/images
 * (proxied blob → object URL), download for everything, loading/empty/error states, "load more".
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Folder, FileText, FileImage, FileVideo, File as FileIcon, FileSpreadsheet,
  Download, ChevronRight, X, Loader2, RefreshCw, AlertCircle, Play,
  DollarSign, TrendingUp, Building2, Receipt, Wallet, BookOpen, Video, ShieldCheck,
} from 'lucide-react';
import { useIsNarrowViewport } from '../../utils/useIsNarrowViewport.js';

// Single on-brand accent for tiles — matches the owner header navy (--color-header-bg-brand).
const BRAND_ACCENT = '#254860';
const BRAND_ACCENT_TINT = 'rgba(37,72,96,0.10)';

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'heic', 'heif', 'tiff', 'tif', 'avif']);
const PDF_EXTS = new Set(['pdf']);
const VIDEO_EXTS = new Set(['mp4', 'mov', 'm4v', 'webm']);
const SHEET_EXTS = new Set(['xls', 'xlsx', 'csv']);

function extOf(name) {
  return String(name || '').split('.').pop().toLowerCase();
}
function isPreviewable(name) {
  const e = extOf(name);
  return PDF_EXTS.has(e) || IMAGE_EXTS.has(e) || VIDEO_EXTS.has(e);
}
function iconFor(entry) {
  if (entry.kind === 'folder') return Folder;
  const e = extOf(entry.name);
  if (IMAGE_EXTS.has(e)) return FileImage;
  if (VIDEO_EXTS.has(e)) return FileVideo;
  if (PDF_EXTS.has(e)) return FileText;
  if (SHEET_EXTS.has(e)) return FileSpreadsheet;
  return FileIcon;
}
// Pick a meaningful icon from a folder's name (keyword heuristics, not exact names),
// so renaming/adding folders degrades gracefully to a clean default — never breaks.
function folderIconFor(name) {
  const n = String(name || '').toLowerCase();
  const has = (...words) => words.some((w) => n.includes(w));
  if (has('revenue', 'payout', 'income', 'earning')) return DollarSign;
  if (has('pricing', 'rate', 'dynamic')) return TrendingUp;
  if (has('operation', 'ownership')) return Building2;
  if (has('tax')) return Receipt;
  if (has('money', 'payment', 'fee', 'financ', 'invoice')) return Wallet;
  if (has('video', 'watch', 'tutorial', 'instruction')) return Video;
  if (has('policy', 'agreement', 'contract', 'legal', 'compliance')) return ShieldCheck;
  if (has('report', 'statement', 'document')) return FileText;
  return BookOpen; // graceful default for anything else
}
function formatSize(bytes) {
  if (bytes == null) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = Number(bytes); let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}
function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return ''; }
}

// File tile cover: real Dropbox thumbnail (works for images, PDFs, and video poster
// frames) with a graceful gradient+icon fallback when none is available. A play badge
// overlays video covers so they read as playable.
function TileCover({ url, Icon, accent, isVideo }) {
  const [failed, setFailed] = useState(!url);
  const playBadge = isVideo ? (
    <span style={{
      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, rgba(15,23,42,0) 40%, rgba(15,23,42,0.28))',
    }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 46, height: 46,
        borderRadius: '50%', background: 'rgba(255,255,255,0.92)', boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
      }}>
        <Play size={20} style={{ color: '#0f172a', marginLeft: 2 }} fill="#0f172a" />
      </span>
    </span>
  ) : null;
  if (failed) {
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${accent}14, ${accent}30)` }}>
        <Icon size={40} strokeWidth={1.5} style={{ color: accent, opacity: 0.85 }} />
        {playBadge}
      </div>
    );
  }
  return (
    <>
      <img src={url} alt="" loading="lazy" onError={() => setFailed(true)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      {playBadge}
    </>
  );
}

/**
 * Reusable Dropbox file explorer. Data-source-agnostic: pass an `api` object so the
 * same component serves both the admin (Property360) and owner-portal surfaces.
 *
 * @param {object}  api          { list({path,cursor,signal}) -> {entries,cursor,has_more,configured},
 *                                 fileBlob(relPath, {download,signal}) -> { blob } | null }
 * @param {string}  rootName     Display label for the root crumb (the folder name)
 * @param {string}  initialPath  Optional subpath to open at (e.g. a top-level folder tile)
 * @param {boolean} tile         Render entries as a card/tile grid instead of a list
 */
export default function DropboxBrowser({ api, rootName, initialPath = '', tile = false }) {
  const isNarrow = useIsNarrowViewport();
  const [path, setPath] = useState(initialPath);
  const [entries, setEntries] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // { name, url, kind: 'pdf'|'image', loading }
  const abortRef = useRef(null);
  const previewUrlRef = useRef(null);

  const loadFolder = useCallback(async (targetPath, { append = false, useCursor = null } = {}) => {
    if (!api) return;
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    try {
      const res = await api.list({ path: targetPath, cursor: useCursor, signal: ac.signal });
      if (res && res.configured === false) {
        setEntries([]); setCursor(null); setHasMore(false);
        setError('This property has no Dropbox folder configured.');
        return;
      }
      setEntries((prev) => (append ? [...prev, ...(res.entries || [])] : (res.entries || [])));
      setCursor(res.cursor || null);
      setHasMore(!!res.has_more);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setError(err?.message || 'Failed to load Dropbox folder');
      if (!append) { setEntries([]); setCursor(null); setHasMore(false); }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadFolder(path);
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [path, loadFolder]);

  // Revoke any object URL on unmount
  useEffect(() => () => { if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current); }, []);

  const openFolder = (entry) => setPath(entry.rel_path);

  const goToCrumb = (idx) => {
    if (idx < 0) { setPath(''); return; }
    const segs = path.split('/').filter(Boolean).slice(0, idx + 1);
    setPath(segs.join('/'));
  };

  const closePreview = useCallback(() => {
    if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; }
    setPreview(null);
  }, []);

  const openFile = useCallback(async (entry) => {
    const e = extOf(entry.name);
    const kind = PDF_EXTS.has(e) ? 'pdf' : IMAGE_EXTS.has(e) ? 'image' : VIDEO_EXTS.has(e) ? 'video' : null;
    if (!kind) { downloadFile(entry); return; }
    // Video: stream from a direct same-origin URL (HTTP Range) instead of buffering
    // the whole file into a blob — large videos otherwise fail on iOS Safari.
    if (kind === 'video' && typeof api.fileUrl === 'function') {
      if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; }
      setPreview({ name: entry.name, url: api.fileUrl(entry.rel_path), kind, loading: false });
      return;
    }
    setPreview({ name: entry.name, url: null, kind, loading: true });
    try {
      const result = await api.fileBlob(entry.rel_path);
      if (!result) { closePreview(); return; }
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const url = URL.createObjectURL(result.blob);
      previewUrlRef.current = url;
      setPreview({ name: entry.name, url, kind, loading: false });
    } catch (err) {
      setPreview({ name: entry.name, url: null, kind, loading: false, error: err?.message || 'Preview failed' });
    }
  }, [api, closePreview]);

  async function downloadFile(entry) {
    try {
      const result = await api.fileBlob(entry.rel_path, { download: true });
      if (!result) return;
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url; a.download = entry.name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { /* surfaced elsewhere if needed */ }
  }

  const crumbs = path.split('/').filter(Boolean);

  return (
    <div className="cpm-dbx">
      {/* Breadcrumb — hidden at root in tile mode (the section heading already names it) */}
      {!(tile && path === '') && (
      <div className="cpm-dbx__crumbs" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem', fontSize: '0.85rem', marginBottom: '0.6rem' }}>
        <button type="button" className="cpm-dbx__crumb" onClick={() => goToCrumb(-1)} style={crumbBtn}>
          <Folder size={14} style={{ marginRight: '0.3rem', verticalAlign: 'text-bottom' }} />{rootName || 'Dropbox'}
        </button>
        {crumbs.map((seg, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
            <ChevronRight size={13} style={{ opacity: 0.5 }} />
            <button type="button" className="cpm-dbx__crumb" onClick={() => goToCrumb(i)} style={crumbBtn}>{seg}</button>
          </span>
        ))}
        <button type="button" onClick={() => loadFolder(path)} title="Refresh" style={{ ...crumbBtn, marginLeft: 'auto' }} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'cpm-spin' : ''} />
        </button>
      </div>
      )}

      {/* Error */}
      {error && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#b42318', fontSize: '0.85rem', padding: '0.5rem 0' }}>
          <AlertCircle size={15} /> {error}
        </div>
      )}

      {/* List / tile grid */}
      {!error && (
        loading && entries.length === 0 ? (
          <div style={{ border: '1px solid #e3e8ef', borderRadius: '8px' }}><div style={rowEmpty}><Loader2 size={16} className="cpm-spin" /> Loading…</div></div>
        ) : entries.length === 0 ? (
          <div style={{ border: '1px solid #e3e8ef', borderRadius: '8px' }}><div style={rowEmpty}>This folder is empty.</div></div>
        ) : tile ? (
          <div className="cpm-dbx__grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(248px, 1fr))', gap: '1rem' }}>
            {entries.map((entry) => {
              const isFolder = entry.kind === 'folder';
              const Icon = isFolder ? folderIconFor(entry.name) : iconFor(entry);
              const accent = BRAND_ACCENT;
              const previewable = !isFolder && isPreviewable(entry.name);
              // Every tile is actionable: folders open, previewable files preview,
              // and non-previewable files download.
              const clickable = true;
              const isVideo = !isFolder && VIDEO_EXTS.has(extOf(entry.name));
              const thumbUrl = (!isFolder && typeof api.thumbUrl === 'function') ? api.thumbUrl(entry.rel_path) : null;
              const cardBase = {
                position: 'relative', display: 'flex', flexDirection: 'column',
                border: '1px solid #e7ebf0', borderRadius: '16px', background: '#fff', overflow: 'hidden',
                boxShadow: '0 1px 2px rgba(16,24,40,0.04)', cursor: clickable ? 'pointer' : 'default',
                transition: 'transform .18s cubic-bezier(.4,0,.2,1), box-shadow .18s, border-color .18s',
              };
              if (isFolder) {
                return (
                  <div key={entry.rel_path} className="olrp-help-card"
                    style={{ ...cardBase, gap: '0.85rem', padding: '1.5rem 1.5rem 1.5rem 1.75rem', minHeight: '152px' }}
                    onClick={() => openFolder(entry)}>
                    {/* brand-blue accent stripe down the left edge */}
                    <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: accent }} />
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 52, height: 52, borderRadius: '14px', flexShrink: 0,
                      background: BRAND_ACCENT_TINT, color: accent,
                    }}>
                      <Icon size={26} strokeWidth={1.9} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '1.02rem', color: '#0f172a', lineHeight: 1.3, wordBreak: 'break-word' }}>{entry.name}</div>
                    </div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.83rem', fontWeight: 600, color: accent }}>
                      Explore <ChevronRight size={15} />
                    </div>
                  </div>
                );
              }
              return (
                <div key={entry.rel_path} className="olrp-help-card" style={cardBase}
                  onClick={() => { if (previewable) openFile(entry); else downloadFile(entry); }}>
                  <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 10', background: '#f1f5f9' }}>
                    <TileCover url={thumbUrl} Icon={Icon} accent={accent} isVideo={isVideo} />
                    <button type="button" title="Download" onClick={(ev) => { ev.stopPropagation(); downloadFile(entry); }}
                      style={{
                        position: 'absolute', top: '0.6rem', right: '0.6rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 30, height: 30, borderRadius: '8px', border: 'none', cursor: 'pointer',
                        background: 'rgba(255,255,255,0.92)', color: '#334155', boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                      }}>
                      <Download size={15} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', padding: '0.9rem 1rem 1rem', flex: 1 }}>
                    <div title={entry.name} style={{
                      fontWeight: 700, fontSize: '0.98rem', color: '#0f172a', lineHeight: 1.3,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>{entry.name}</div>
                    <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{formatSize(entry.size)}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.83rem', fontWeight: 600, color: accent }}>
                        {isVideo ? 'Play' : previewable ? 'Open' : 'Download'} <ChevronRight size={15} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="cpm-dbx__list" style={{ border: '1px solid #e3e8ef', borderRadius: '8px', overflow: 'hidden' }}>
            {entries.map((entry) => {
              const Icon = iconFor(entry);
              const clickable = entry.kind === 'folder' || isPreviewable(entry.name);
              return (
                <div
                  key={entry.rel_path}
                  className="cpm-dbx__row"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.75rem', borderTop: '1px solid #f0f3f7', cursor: clickable ? 'pointer' : 'default' }}
                  onClick={() => { if (entry.kind === 'folder') openFolder(entry); else if (isPreviewable(entry.name)) openFile(entry); }}
                >
                  <Icon size={18} strokeWidth={1.8} style={{ color: entry.kind === 'folder' ? '#3b82f6' : '#64748b', flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name}</span>
                  {entry.kind === 'file' && (
                    <>
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>{formatDate(entry.modified)}</span>
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8', whiteSpace: 'nowrap', minWidth: '54px', textAlign: 'right' }}>{formatSize(entry.size)}</span>
                      <button type="button" title="Download" onClick={(ev) => { ev.stopPropagation(); downloadFile(entry); }} style={iconBtn}>
                        <Download size={15} />
                      </button>
                    </>
                  )}
                  {entry.kind === 'folder' && <ChevronRight size={15} style={{ color: '#cbd5e1' }} />}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Load more */}
      {hasMore && !error && (
        <div style={{ textAlign: 'center', marginTop: '0.6rem' }}>
          <button type="button" className="cpm-btn cpm-btn--secondary" onClick={() => loadFolder(path, { append: true, useCursor: cursor })} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {/* Preview modal — portaled to body to escape any ancestor stacking context */}
      {preview && createPortal((
        <div
          role="dialog"
          aria-modal="true"
          onClick={closePreview}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isNarrow ? 0 : '2rem' }}
        >
          <div onClick={(ev) => ev.stopPropagation()} style={{ background: '#fff', borderRadius: isNarrow ? 0 : '10px', width: isNarrow ? '100%' : 'min(960px, 95vw)', height: isNarrow ? '100%' : 'min(90vh, 1000px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', borderBottom: '1px solid #e3e8ef' }}>
              <span style={{ flex: 1, fontWeight: 600, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview.name}</span>
              <button type="button" className="cpm-btn cpm-btn--secondary" onClick={() => downloadFile({ name: preview.name, rel_path: (path ? `${path}/` : '') + preview.name })}>
                <Download size={14} style={{ marginRight: '0.3rem', verticalAlign: 'text-bottom' }} /> Download
              </button>
              <button type="button" onClick={closePreview} title="Close" style={iconBtn}><X size={18} /></button>
            </div>
            <div style={{ flex: 1, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
              {preview.loading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#64748b' }}><Loader2 size={18} className="cpm-spin" /> Loading preview…</span>
              ) : preview.error ? (
                <span style={{ color: '#b42318', display: 'flex', alignItems: 'center', gap: '0.4rem' }}><AlertCircle size={16} /> {preview.error}</span>
              ) : preview.kind === 'pdf' ? (
                <iframe title={preview.name} src={preview.url} style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : preview.kind === 'video' ? (
                <video src={preview.url} controls playsInline preload="metadata" style={{ width: '100%', maxWidth: '100%', maxHeight: '100%' }} />
              ) : (
                <img alt={preview.name} src={preview.url} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}

const crumbBtn = { background: 'none', border: 'none', color: '#334e68', cursor: 'pointer', padding: '0.15rem 0.3rem', borderRadius: '4px', fontSize: '0.85rem' };
const iconBtn = { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0.25rem', borderRadius: '4px', display: 'inline-flex' };
const rowEmpty = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '1.5rem', color: '#94a3b8', fontSize: '0.875rem' };
